import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnChanges,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';

// services
import { TranslateService } from '@ngx-translate/core';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { AuthService } from '@app/shared/services/auth.service';

// types
import { TagsApi } from '@app/tags/tag-api.model';
import { InternalTagRatingTypes } from '@app/tags/tags';
import { TagRatingButtonService } from '@app/tags/components/tag-rating-button/tag-rating-button.service';

@Component({
  selector: 'dgx-skill-signal-ratings',
  templateUrl: './skill-signal-ratings.component.html',
  styleUrls: ['./skill-signal-ratings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SkillSignalRatings
  extends SubscriberBaseDirective
  implements AfterViewInit, OnChanges
{
  @Input() public tag: TagsApi.Tag;
  @Input() public ratingDetails: TagsApi.UserTagRatingDetails[];
  @Input() public isTagEvaluable: boolean;
  @Input() public availableRatings: TagsApi.RatingType[];
  @Input() public ownerIsViewing: boolean;
  @Input() public isMobile: boolean;
  @Input() public isLoading: boolean;
  @Input() public signalType: string;
  @Input() public allCheckpoints: TagsApi.Checkpoint[];

  @ViewChildren('ratingCard')
  public ratingCards: QueryList<ElementRef>;

  public numRatingsLimit = 3;
  public trackingLocation: string = 'signals';
  public i18n = this.translateService.instant([
    'Core_Ratings',
    'Core_ShowMore',
    'dgTagRating_Evaluation',
    'dgTagRating_SelfAssessment',
    'dgTagRating_ManagerAssessment',
    'dgTagRating_EndorsedByFormat',
    'dgSkillSignalRatings_PrivacyInfo',
    'dgTagRating_PeerRatings',
  ]);

  private cardsPerRow = 3;
  private threeRows = this.cardsPerRow * 3;

  constructor(
    private translateService: TranslateService,
    private authService: AuthService,
    private tagRatingButtonService: TagRatingButtonService
  ) {
    super();
  }

  public ngAfterViewInit() {
    this.ratingCards.changes.subscribe(() => {
      if (this.ratingCards.length > this.cardsPerRow) {
        this.ratingCards.toArray()[this.cardsPerRow].nativeElement.focus();
      }
    });
  }

  public get types(): { [key: string]: string } {
    return InternalTagRatingTypes;
  }

  public get tagRatingDetails(): TagsApi.UserTagRatingDetails[] {
    // tag rating details (TagsApi.UserTagRatingDetails - includes raters/endorsers) aren't
    // available for unauthenticated users so fall back to tag ratings (TagsApi.UserTagRating)
    return this.authService?.authUser
      ? this.ratingDetails
      : (this.tag?.ratings as TagsApi.UserTagRatingDetails[]);
  }

  public get sortedRatings(): string[] {
    const sortedRatings = this.tagRatingButtonService.getEnabledRatingTypes(
      this.tagRatingDetails,
      true /* sort by rank */,
      this.ownerIsViewing,
      this.availableRatings,
      this.allCheckpoints,
      this.isTagEvaluable
    );

    // Placeholder blocks
    let emptyTitleLength =
      this.cardsPerRow * Math.ceil(sortedRatings.length / this.cardsPerRow) -
      sortedRatings.length;

    if (sortedRatings.length === 0) {
      emptyTitleLength = 3;
    }

    if (this.isMobile) {
      emptyTitleLength = sortedRatings.length === 0 ? 1 : 0;
    }

    for (let i = 0; i < emptyTitleLength; i++) {
      sortedRatings.push('Empty');
    }

    if (this.isMobile) {
      this.numRatingsLimit = sortedRatings.length;
    }

    return sortedRatings;
  }

  public ngOnChanges({ signalType }: SimpleChanges): void {
    if (!this.isMobile && signalType?.currentValue) {
      this.numRatingsLimit = { Ratings: this.threeRows, All: this.cardsPerRow }[
        signalType.currentValue
      ];
    }
  }

  public trackByFn(_: number, type: string): string {
    return type;
  }

  public loadMoreRatings(): void {
    if (this.numRatingsLimit === this.cardsPerRow) {
      this.numRatingsLimit = this.threeRows;
    } else {
      this.numRatingsLimit = this.numRatingsLimit + this.threeRows;
    }
  }
}
